/**
 * Bundle groups inside the Essential Cart drawer.
 *
 * The Essential Cart app renders its own drawer into #essential-cart-drawer-portal.
 * This element watches that drawer, matches its rows against /cart.js and, for
 * line items sharing a hidden `_bundle_id` property, inserts one collapsible
 * header row per set (collage, title, piece count, view/edit toggle, total).
 * When the cart holds exactly one set it also shows the "add another set" offer.
 *
 * Copy, colours and behaviour come from Theme settings › Cart › Product bundles
 * (see snippets/jewelique-bundle-cart.liquid).
 */
(() => {
  if (customElements.get('jewelique-bundle-cart')) return;

  const element = (tag, cls, text) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  const selector = (name) => `[data-essential-cart-element="${name}"]`;

  class JeweliqueBundleCart extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.strings = JSON.parse(this.querySelector('[data-jq-strings]').textContent);
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/'); // "/en" has no trailing slash
      this.cartUrl = this.dataset.cartUrl || `${this.root}cart`;
      this.shopCurrency = this.dataset.shopCurrency || '';
      // Storefront money format (e.g. "Dhs. [amount]") so group totals match the drawer's own prices.
      this.moneyTemplate = this.dataset.moneyFormat && this.dataset.moneyFormat.includes('[amount]') ? this.dataset.moneyFormat : null;
      this.number = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      this.offerAmount = parseInt(this.dataset.offerAmount, 10) || 0;
      this.offerEnabled = this.dataset.offerEnabled !== 'false';
      this.warned = false;
      this.renderedCurrency = this.dataset.currency || this.shopCurrency;
      this.collapsedByDefault = this.dataset.collapsed !== 'false';
      this.expanded = new Map();
      this.lastRows = [];
      this.serial = 0;
      this.cartFreshAt = 0;
      this.interceptFetch();

      document.addEventListener('jewelique:cart', (event) => {
        if (event.detail && event.detail !== this.cart) {
          this.cart = event.detail;
          this.safeRender();
        }
      });

      const discover = () => {
        const portal = document.querySelector('#essential-cart-drawer-portal');
        if (!portal) return false;
        this.portal = portal;
        this.observer = new MutationObserver((records) => {
          const changed = records.some((record) => {
            if (record.target.closest?.('.jq-cart-group,.jq-cart-offer')) return false;
            const nodes = [...record.addedNodes, ...record.removedNodes];
            if (nodes.length && nodes.every((n) => n.nodeType === 1 && n.matches?.('.jq-cart-group,.jq-cart-offer'))) return false;
            if (record.type === 'attributes') return record.target.matches?.('[role="dialog"]');
            return (
              record.target.closest?.(selector('cart-drawer-item-list')) ||
              nodes.some(
                (n) =>
                  n.nodeType === 1 &&
                  (n.matches?.(selector('cart-drawer-item-list')) || n.querySelector?.(selector('cart-drawer-item-list')))
              )
            );
          });
          if (changed) this.schedule();
        });
        this.observer.observe(portal, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
        portal.addEventListener('click', (event) => this.onRemoveClick(event), true);
        this.schedule();
        return true;
      };

      if (!discover()) {
        // The app injects its portal early; stop looking a while after load.
        const observer = new MutationObserver(() => {
          if (discover()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        const stop = () => setTimeout(() => observer.disconnect(), 15000);
        if (document.readyState === 'complete') stop();
        else window.addEventListener('load', stop, { once: true });
      }
    }

    t(key) {
      const value = this.strings[key];
      return value == null ? '' : String(value);
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 140);
    }

    async refresh() {
      if (this.loading) {
        this.again = true;
        return;
      }
      if (this.cart && Date.now() - this.cartFreshAt < 1500) {
        // The drawer just redrew after a cart call we already saw; no need to ask Shopify again.
        this.safeRender();
        return;
      }
      this.loading = true;
      try {
        const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        this.adopt(await response.json());
      } catch (error) {
        this.restore();
        this.warn(error);
      } finally {
        this.loading = false;
        if (this.again) {
          this.again = false;
          this.schedule();
        }
      }
    }

    /** Takes a full cart object (from /cart.js, change.js, update.js or clear.js) as the current truth. */
    adopt(cart) {
      if (!cart || !Array.isArray(cart.items) || typeof cart.total_price !== 'number') return;
      this.cart = cart;
      this.cartFreshAt = Date.now();
      this.safeRender();
      document.dispatchEvent(new CustomEvent('jewelique:cart', { detail: cart }));
    }

    /**
     * The Essential Cart app and the theme already call Shopify's cart endpoints on
     * every change. Reading those responses saves a second round trip per change,
     * so the set rows regroup as soon as the drawer redraws.
     */
    interceptFetch() {
      if (window.__jqCartFetchPatched || typeof window.fetch !== 'function') return;
      window.__jqCartFetchPatched = true;
      const original = window.fetch;
      const fullCart = /\/cart(?:\/(?:change|update|clear))?\.js(?:[?#]|$)/;
      window.fetch = (...args) => {
        const promise = original.apply(window, args);
        try {
          const input = args[0];
          const url = typeof input === 'string' ? input : input?.url || '';
          if (fullCart.test(url)) {
            promise
              .then((response) => {
                if (!response.ok) return;
                response
                  .clone()
                  .json()
                  .then((json) => this.adopt(json))
                  .catch(() => {});
              })
              .catch(() => {});
          }
        } catch (error) {
          /* never interfere with the original call */
        }
        return promise;
      };

      // Apps that still use XMLHttpRequest for cart calls.
      const XHR = window.XMLHttpRequest;
      if (XHR && XHR.prototype && !XHR.prototype.__jqCartPatched) {
        XHR.prototype.__jqCartPatched = true;
        const open = XHR.prototype.open;
        const send = XHR.prototype.send;
        const self = this;
        XHR.prototype.open = function (method, url, ...rest) {
          this.__jqCartCall = fullCart.test(String(url));
          return open.call(this, method, url, ...rest);
        };
        XHR.prototype.send = function (...args) {
          if (this.__jqCartCall) {
            this.addEventListener('load', () => {
              try {
                if (this.status < 200 || this.status >= 300) return;
                const body = this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
                self.adopt(body);
              } catch (error) {
                /* not a cart payload */
              }
            });
          }
          return send.apply(this, args);
        };
      }
    }

    /**
     * The app's own remove button runs a slow path (its request, then extra work
     * before it redraws). Take the click over for rows we know: hide the row at
     * once and send the change ourselves; the app redraws from that response.
     */
    onRemoveClick(event) {
      const button = event.target.closest?.(selector('line-item-remove'));
      if (!button || this.removing) return;
      const row = button.closest(selector('cart-drawer-item'));
      const key = row?.dataset.jqKey;
      if (!key || !this.cart?.items?.some((item) => item.key === key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.removeLines([key], [row]);
    }

    /** Removes every piece of a set in one request; the rows disappear immediately. */
    removeSet(key, members, header) {
      if (this.removing) return;
      header.hidden = true;
      this.expanded.delete(key);
      this.removeLines(
        members.map(({ item }) => item.key),
        members.map(({ row }) => row),
        () => {
          header.hidden = false;
        }
      );
    }

    async removeLines(keys, rows, onFail) {
      this.removing = true;
      rows.forEach((row) => {
        row.dataset.jqRemoved = '1';
        row.classList.add('jq-cart-member--hidden');
      });
      const updates = {};
      keys.forEach((k) => {
        updates[k] = 0;
      });
      try {
        const response = await fetch(`${this.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!response.ok) throw new Error(`cart/update.js ${response.status}`);
        const cart = await response.json();
        this.adopt(cart);
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jewelique-bundle-cart', cartData: cart });
        }
        // The app redraws its rows from the cart call it saw; if it has not, reload so the drawer stays truthful.
        setTimeout(() => {
          if (rows.some((row) => row.isConnected)) window.location.reload();
        }, 2500);
      } catch (error) {
        rows.forEach((row) => {
          delete row.dataset.jqRemoved;
          row.classList.remove('jq-cart-member--hidden');
        });
        if (onFail) onFail();
        this.warn(error);
      } finally {
        this.removing = false;
      }
    }

    /** Any failure leaves the Essential Cart drawer exactly as the app rendered it. */
    safeRender() {
      try {
        this.render();
      } catch (error) {
        this.restore();
        this.warn(error);
      }
    }

    warn(error) {
      if (this.warned) return;
      this.warned = true;
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[jewelique-bundle-cart] Bundle grouping disabled for this cart render:', error);
      }
    }

    /**
     * The offer threshold in cents. Sets added from the bundles section carry the
     * threshold and currency they were sold under (`_bundle_offer`, `_bundle_currency`);
     * a stamp only counts while the cart is still in that currency. The theme
     * setting (shop currency) is the fallback for older carts.
     */
    offerFor(items) {
      const stamped = items
        .filter((item) => (item.properties?._bundle_currency || this.shopCurrency) === (this.cart.currency || this.shopCurrency))
        .map((item) => parseInt(item.properties?._bundle_offer, 10))
        .find((value) => Number.isFinite(value) && value > 0);
      if (stamped) return { amount: stamped, stamped: true };
      return { amount: this.offerAmount, stamped: false };
    }

    restore() {
      this.portal?.querySelectorAll('.jq-cart-group,.jq-cart-offer').forEach((n) => n.remove());
      this.portal?.querySelectorAll('.jq-cart-member').forEach((row) => {
        row.classList.remove('jq-cart-member');
        if (!row.dataset.jqRemoved) row.classList.remove('jq-cart-member--hidden');
      });
      this.fingerprint = null;
    }

    /**
     * Sum of the members' line prices, unless the set was sold at a declared price
     * that the sum only misses because Shopify rounded each line's discount share
     * (three-decimal currencies: 19.900 becomes 6.63 x 3 = 19.89).
     */
    groupTotal(members) {
      const sum = members.reduce((n, { item }) => n + item.final_line_price, 0);
      const first = members[0].item.properties || {};
      const declared = parseInt(first._bundle_price, 10);
      const currency = first._bundle_currency || this.shopCurrency;
      const single = members.every(({ item }) => item.quantity === 1);
      if (declared > 0 && single && currency === (this.cart.currency || this.shopCurrency) && Math.abs(sum - declared) <= members.length) {
        return declared;
      }
      return sum;
    }

    money() {
      // Only trust the Liquid template while the cart is still in the currency it was rendered for.
      if (this.moneyTemplate && (!this.cart.currency || this.cart.currency === this.renderedCurrency)) {
        return { format: (amount) => this.moneyTemplate.replace('[amount]', this.number.format(amount)) };
      }
      return new Intl.NumberFormat(this.lang === 'ar' ? 'ar-AE' : 'en-AE', {
        style: 'currency',
        currency: this.cart.currency || this.shopCurrency || 'AED',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    holdsOneSet(items) {
      if (!items.length) return false;
      const bundleItems = items.filter((item) => item.properties && item.properties._bundle_id);
      if (bundleItems.length !== items.length) return false;
      const ids = new Set(bundleItems.map((item) => item.properties._bundle_id));
      const size = parseInt(bundleItems[0].properties._bundle_size, 10) || 3;
      return ids.size === 1 && bundleItems.length === size && bundleItems.every((item) => item.quantity === 1);
    }

    render() {
      if (!this.portal || !this.cart?.items) return;
      const list = this.portal.querySelector(selector('cart-drawer-item-list'));
      if (!list) return;
      const rows = [...list.querySelectorAll(selector('cart-drawer-item'))];
      const items = this.cart.items;
      const valid =
        rows.length === items.length &&
        rows.every((row, i) => {
          const link = row.querySelector(selector('line-item-title'));
          const quantity = row.querySelector(selector('quantity-value'));
          return (
            link &&
            new URL(link.href, location.origin).searchParams.get('variant') === String(items[i].variant_id) &&
            (!quantity || Number(quantity.textContent.trim()) === items[i].quantity)
          );
        });
      if (!valid) {
        this.restore();
        return;
      }

      const fingerprint = JSON.stringify(
        items.map((item) => [item.key, item.quantity, item.final_line_price, item.properties?._bundle_id, item.properties?._bundle_title])
      );
      if (this.fingerprint === fingerprint && rows.every((row, i) => row === this.lastRows[i]) && list.querySelector('.jq-cart-group')) return;

      this.restore();
      this.fingerprint = fingerprint;
      this.lastRows = rows;
      rows.forEach((row, i) => {
        row.dataset.jqKey = items[i].key;
      });

      const groups = new Map();
      items.forEach((item, i) => {
        const key = item.properties?._bundle_id;
        if (!key) return;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ item, row: rows[i] });
      });
      const money = this.money();

      for (const [key, members] of groups) {
        const first = members[0].item;
        const title = first.properties._bundle_title || this.t('set');
        const count = members.reduce((n, { item }) => n + item.quantity, 0);
        const total = this.groupTotal(members);
        const header = element('div', 'jq-cart-group');
        header.dir = this.dir;
        header.lang = this.lang;
        const button = element('button', 'jq-cart-group__button');
        button.type = 'button';
        const collage = element('span', 'jq-cart-group__images');
        members.slice(0, 3).forEach(({ item }) => {
          if (!item.image) return;
          const img = element('img');
          img.src = item.image;
          img.alt = '';
          img.width = 40;
          img.height = 40;
          collage.append(img);
        });
        const content = element('span', 'jq-cart-group__content');
        content.append(element('strong', '', title));
        content.append(element('span', 'jq-cart-group__count', this.t('piece_count').replace('[count]', String(count))));
        const text = element('span', 'jq-cart-group__edit');
        content.append(text);
        const price = element('bdi', 'jq-cart-group__price', money.format(total / 100));
        button.append(collage, content);
        // Price and "Remove set" share the end column so the group stays as tall as its header.
        const aside = element('span', 'jq-cart-group__aside');
        aside.append(price);
        header.append(button, aside);
        if (this.t('remove_set')) {
          const remove = element('button', 'jq-cart-group__remove', this.t('remove_set'));
          remove.type = 'button';
          remove.addEventListener('click', () => this.removeSet(key, members, header));
          aside.append(remove);
        }

        const ids = members.map(({ row }) => {
          if (!row.id) row.id = `jq-cart-member-${++this.serial}`;
          row.classList.add('jq-cart-member');
          return row.id;
        });
        button.setAttribute('aria-controls', ids.join(' '));

        const apply = () => {
          const open = this.expanded.get(key) ?? (this.collapsedByDefault ? members.length !== 3 : true);
          button.setAttribute('aria-expanded', String(open));
          text.textContent = this.t(open ? 'hide_items' : 'show_items');
          members.forEach(({ row }) => row.classList.toggle('jq-cart-member--hidden', !open));
        };
        button.addEventListener('click', () => {
          this.expanded.set(key, button.getAttribute('aria-expanded') !== 'true');
          apply();
        });
        members[0].row.parentElement.insertBefore(header, members[0].row);
        apply();
      }

      const offerAmount = this.offerFor(items);
      const remaining = offerAmount.amount - this.cart.total_price;
      if (
        this.offerEnabled &&
        groups.size &&
        offerAmount.amount > 0 &&
        (offerAmount.stamped || this.cart.currency === this.shopCurrency) &&
        this.holdsOneSet(items) &&
        remaining > 0 &&
        this.t('upsell')
      ) {
        const offer = element('div', 'jq-cart-offer');
        offer.dir = this.dir;
        offer.append(element('strong', '', this.t('upsell').replace('[amount]', money.format(remaining / 100))));
        if (this.t('upsell_detail')) {
          offer.append(element('p', '', this.t('upsell_detail').replace('[amount]', money.format(offerAmount.amount / 100))));
        }
        if (this.t('choose_second')) {
          const link = element('a', 'jq-cart-offer__link', this.t('choose_second'));
          link.href = `${this.root}#jewelique-bundles`;
          link.dataset.cartUrl = this.cartUrl;
          link.addEventListener('click', (event) => {
            const section = document.querySelector('jewelique-bundles');
            if (!section) return;
            event.preventDefault();
            this.portal.querySelector(selector('cart-drawer-close-button'))?.click();
            requestAnimationFrame(() => {
              section.scrollIntoView({ behavior: 'smooth', block: 'start' });
              section.querySelector('[data-jq-open]')?.focus({ preventScroll: true });
            });
          });
          offer.append(link);
        }
        list.before(offer);
      }
    }
  }

  customElements.define('jewelique-bundle-cart', JeweliqueBundleCart);
})();
